<template>
  <div></div>
</template>

<script>
import logger from "@/shared/log";
import loadKendoUI from "@/shared/setup/kendo-ui";
import loadChat from "@/shared/setup/chat";
import loadAdapterjs from "@/shared/setup/adapterjs";
import loadNanoscroller from "@/shared/setup/nanoscroller";
import cloneDeep from "lodash/cloneDeep";
import chatUtils from "../shared/chat-utils";
import globalEvents from "../shared/global-events";
import notify from "@/shared/notify";
import { VisGlobalEventTypes } from "@/shared/enum/global-event-types";

const LoadStatus = Object.freeze({
  Ghost: 1,
  Loading: 2,
  Loaded: 3,
  Error: 4
});

const DEFAULT_ICON = `${location.origin}/img/chat/assets/images/default-user.svg`;

function loadLazyProp(lazyProperty) {
  return new Promise((resolve, reject) => {
    const lazyState = lazyProperty.lazyState;

    if (lazyState === LoadStatus.Loaded) {
      resolve(lazyProperty);
    } else if (lazyState === LoadStatus.Error) {
      reject(new Error("Load propetry failed"));
    } else {
      lazyProperty.lazyWait.push((obj, lazyErr) => {
        if (lazyProperty.lazyState === LoadStatus.Loaded) {
          resolve(lazyProperty);
        } else {
          reject(lazyErr);
        }
      });
    }
  });
}

export default {
  name: "vis-chat",
  props: {
    chatApi: {
      type: String,
      default: ""
    },
    authApi: {
      type: String,
      default: ""
    },
    visaryApi: {
      type: String,
      default: ""
    },
    iceServers: {
      type: String,
      default: ""
    },
    assetsPath: {
      type: String,
      default: ""
    },
    notify: {
      type: Boolean,
      default: true
    },
    logUrl: {
      type: String,
      default: null
    },
    logLevel: {
      type: [String, Number],
      default: null
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    isEmployee: {
      type: Boolean,
      default: false
    },
    isManager: {
      type: Boolean,
      default: false
    },
    isOperator: {
      type: Boolean,
      default: false
    },
    isSupervisor: {
      type: Boolean,
      default: false
    },
  },
  computed: {
    reinitProps() {
      return {
        chatApi: this.chatApi,
        authApi: this.authApi,
        visaryApi: this.visaryApi,
        iceServers: this.iceServers,
        isAdmin: this.isAdmin,
        isEmployee: this.isEmployee,
        isManager: this.isManager,
        isOperator: this.isOperator,
        isSupervisor: this.isSupervisor,
      };
    }
  },
  watch: {
    reinitProps() {
      this.destroyChat();
      this.initWidget();
    }
  },
  created() {
    this.setupGlobalEvents();
  },
  mounted() {
    this.initAsync();

    if (this.notify) {
      notify.checkPermissions();
    }
  },
  destroyed() {
    this.removeGlobalEvents();
    this.destroyChat();
  },
  methods: {
    destroyChat() {
      if (this.$_chat) {
        this.$_chat.destroy();
        this.$_chat = null;
      }
    },
    async initAsync() {
      try {
        await loadAdapterjs();
        await loadNanoscroller();
        await loadKendoUI();
        await loadChat();

        this.$nextTick(this.initWidget);
      } catch (e) {
        log.error(e);
        this.emitLoaded(true);
      }
    },
    initWidget() {
      const options = {
        ...cloneDeep(this.$props),
        opt: {
          log: {
            logUrl: this.logUrl,
            logLevel: this.logLevel
          }
        }
      };
      this.$_chat = this.createChat(this.$el, options)
        .bind("init", () => {
          this.emitLoaded(false);

          const chatVm = this.$_chat.controlVm.getViewModel();
          if (chatVm) {
            chatVm.client.addChatMessageListener(this.handleReceiveMessage);
          }
        })
        .bind("userpreview", (e) => {
          this.$emit("userpreview", { ...e });
          globalEvents.emit(VisGlobalEventTypes.USER_PREVIEW, e.userId);
        });
    },
    createChat(domElement, options) {
      return new window.kendo.ui.ChitChat(domElement, {
        ...options,
        customFetchToken: chatUtils.fetchToken,
        customRefreshToken: chatUtils.refreshToken
      });
    },
    setupGlobalEvents() {
      globalEvents.on(
        VisGlobalEventTypes.VIDEO_CALL,
        this.handleVideoCallEvent
      );
      globalEvents.on(
        VisGlobalEventTypes.AUDIO_CALL,
        this.handleAudioCallEvent
      );
      globalEvents.on(
        VisGlobalEventTypes.OPEN_CHAT_WITH,
        this.handleOpenChatWithEvent
      );
    },
    removeGlobalEvents() {
      globalEvents.off(
        VisGlobalEventTypes.VIDEO_CALL,
        this.handleVideoCallEvent
      );
      globalEvents.off(
        VisGlobalEventTypes.AUDIO_CALL,
        this.handleAudioCallEvent
      );
      globalEvents.off(
        VisGlobalEventTypes.OPEN_CHAT_WITH,
        this.handleOpenChatWithEvent
      );
    },
    emitLoaded(error = false) {
      this.$emit("loaded", error);
    },
    isOpened() {
      if (!this.$_chat) return false;
      return this.$_chat.controlVm.isOpened;
    },
    unreadMessageCount() {
      if (!this.$_chat) return 0;
      return this.$_chat.controlVm.unreadTotal;
    },
    open() {
      if (!this.$_chat) return;
      return this.$_chat.controlVm.openChat();
    },
    close() {
      if (!this.$_chat) return;
      return this.$_chat.controlVm.closeChat();
    },
    toggle() {
      if (!this.$_chat) return;
      return this.$_chat.controlVm.toggleChat();
    },
    openWith(userId) {
      if (!this.$_chat) return;
      return this.$_chat.controlVm.openChatWith(userId);
    },
    videoCall(userId) {
      if (!this.$_chat) return;
      return this.$_chat.controlVm.videoCall(userId);
    },
    audioCall(userId) {
      if (!this.$_chat) return;
      return this.$_chat.controlVm.audioCall(userId);
    },
    handleVideoCallEvent(userId) {
      if (userId) {
        this.videoCall(userId);
      }
    },
    handleAudioCallEvent(userId) {
      if (userId) {
        this.audioCall(userId);
      }
    },
    handleOpenChatWithEvent(userId) {
      if (userId) {
        this.openWith(userId);
      }
    },
    handleReceiveMessage(message, channel) {
      if (this.notify) {
        this.showBrowserNotification(message);
      }

      this.$emit("receive-message", {
        message,
        channel
      });
    },
    async showBrowserNotification(message) {
      let title = this.$t('incomingMessage');
      let icon = DEFAULT_ICON;
      const text = message.Content;

      try {
        const sender = await loadLazyProp(message.Sender);
        title = sender.FullName;
        icon = sender.ImageUrl
          ? `${location.origin}${sender.ImageUrl}`
          : DEFAULT_ICON;
      } catch (e) {
        logger.error(e);
      }

      notify(title, text, icon);
    }
  }
};
</script>
